/**
 * img-comparison-slider standalone component
 * Adapted from: https://github.com/sneas/img-comparison-slider (v8.0.6)
 *
 * This is a standalone version of the img-comparison-slider web component
 * that doesn't require bundling or npm imports.
 */

if (!customElements.get('img-comparison-slider')) {
  // Inject required styles into the document
  const styleEl = document.createElement('style');
  styleEl.textContent = `
    img-comparison-slider {
      visibility: hidden;
    }
    img-comparison-slider [slot='second'] {
      display: none;
    }
    img-comparison-slider.rendered {
      visibility: inherit;
    }
    img-comparison-slider.rendered [slot='second'] {
      display: unset;
    }
    /* Custom styling for black divider (used with .slider-divider class) */
    img-comparison-slider.slider-divider {
      --divider-color: #000;
    }
    /* Additional custom styling from legacy CSS */
    img-comparison-slider {
      --handle-position-start: 85%;
    }
    img-comparison-slider:focus,
    img-comparison-slider::part(slider) {
      outline: none;
    }
  `;
  document.head.appendChild(styleEl);

  // Template for the shadow DOM
  const template = document.createElement('template');
  template.innerHTML = `
    <style>
      :host {
        --divider-width: 1px;
        --divider-color: #fff;
        --divider-shadow: none;
        --default-handle-width: 50px;
        --default-handle-color: #fff;
        --default-handle-opacity: 1;
        --default-handle-shadow: none;
        --handle-position-start: 50%;
        position: relative;
        display: inline-block;
        overflow: hidden;
        line-height: 0;
        direction: ltr;
      }

      @media screen and (-webkit-min-device-pixel-ratio: 0) and (min-resolution: 0.001dpcm) {
        :host {
          outline-offset: 1px;
        }
      }

      :host(:focus) {
        outline: 2px solid -webkit-focus-ring-color;
      }

      ::slotted(*) {
        -webkit-user-drag: none;
        -khtml-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
        user-drag: none;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }

      .first {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        line-height: normal;
        font-size: 100%;
        max-height: 100%;
        height: 100%;
        width: 100%;
        --exposure: 50%;
        --keyboard-transition-time: 0ms;
        --default-transition-time: 0ms;
        --transition-time: var(--default-transition-time);
      }

      .first .first-overlay-container {
        position: relative;
        clip-path: inset(0 var(--exposure) 0 0);
        transition: clip-path var(--transition-time);
        height: 100%;
      }

      .first .first-overlay {
        overflow: hidden;
        height: 100%;
      }

      .first.focused {
        will-change: clip-path;
      }

      .first.focused .first-overlay-container {
        will-change: clip-path;
      }

      .second {
        position: relative;
      }

      .handle-container {
        transform: translateX(50%);
        position: absolute;
        top: 0;
        right: var(--exposure);
        height: 100%;
        transition: right var(--transition-time), bottom var(--transition-time);
      }

      .focused .handle-container {
        will-change: right;
      }

      .divider {
        position: absolute;
        height: 100%;
        width: 100%;
        left: 0;
        top: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
      }

      .divider:after {
        content: ' ';
        display: block;
        height: 100%;
        border-left-width: var(--divider-width);
        border-left-style: solid;
        border-left-color: var(--divider-color);
        box-shadow: var(--divider-shadow);
      }

      .handle {
        position: absolute;
        top: var(--handle-position-start);
        pointer-events: none;
        box-sizing: border-box;
        margin-left: 1px;
        transform: translate(calc(-50% - 0.5px), -50%);
        line-height: 0;
      }

      .default-handle {
        width: var(--default-handle-width);
        opacity: var(--default-handle-opacity);
        transition: all 1s;
        filter: drop-shadow(var(--default-handle-shadow));
      }

      .default-handle path {
        stroke: var(--default-handle-color);
      }

      .vertical .first-overlay-container {
        clip-path: inset(0 0 var(--exposure) 0);
      }

      .vertical .handle-container {
        transform: translateY(50%);
        height: auto;
        top: unset;
        bottom: var(--exposure);
        width: 100%;
        left: 0;
        flex-direction: row;
      }

      .vertical .divider:after {
        height: 1px;
        width: 100%;
        border-top-width: var(--divider-width);
        border-top-style: solid;
        border-top-color: var(--divider-color);
        border-left: 0;
      }

      .vertical .handle {
        top: auto;
        left: var(--handle-position-start);
        transform: translate(calc(-50% - 0.5px), -50%) rotate(90deg);
      }
    </style>
    <div class="second" id="second">
      <slot name="second"><slot name="before"></slot></slot>
    </div>
    <div class="first" id="first">
      <div class="first-overlay">
        <div class="first-overlay-container" id="firstImageContainer">
          <slot name="first"><slot name="after"></slot></slot>
        </div>
      </div>
      <div class="handle-container">
        <div class="divider"></div>
        <div class="handle" id="handle">
          <slot name="handle">
            <svg xmlns="http://www.w3.org/2000/svg" class="default-handle" viewBox="-8 -3 16 6">
              <path d="M -5 -2 L -7 0 L -5 2 M 5 -2 L 7 0 L 5 2" fill="none" vector-effect="non-scaling-stroke"/>
            </svg>
          </slot>
        </div>
      </div>
    </div>
  `;

  const RENDERED_CLASS = 'rendered';
  const DIRECTIONS = ['horizontal', 'vertical'];
  const ARROW_KEYS = { ArrowLeft: -1, ArrowRight: 1 };

  const isElementAffected = (element, event) => {
    const rect = element.getBoundingClientRect();
    let x, y;
    if (event.type === 'mousedown') {
      x = event.clientX;
      y = event.clientY;
    } else {
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    }
    return x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height;
  };

  const getTouchPagePoint = (e) => ({
    x: e.touches[0].pageX,
    y: e.touches[0].pageY
  });

  const getMousePagePoint = (e) => ({
    x: e.pageX,
    y: e.pageY
  });

  const clamp = (value, min = 0, max = 100) => (value < min ? min : value > max ? max : value);

  customElements.define(
    'img-comparison-slider',
    class ImgComparisonSlider extends HTMLElement {
      constructor() {
        super();
        this.exposure = this.hasAttribute('value') ? parseFloat(this.getAttribute('value')) : 50;
        this.slideOnHover = false;
        this.slideDirection = 'horizontal';
        this.keyboard = 'enabled';
        this.isMouseDown = false;
        this.animationDirection = 0;
        this.isFocused = false;
        this.dragByHandle = false;
        this.bodyUserSelectStyle = '';
        this.bodyWebkitUserSelectStyle = '';
        this.touchStartPoint = null;
        this.isTouchComparing = false;
        this.hasTouchMoved = false;
        this.transitionTimer = null;
        this.imageWidth = 0;
        this.imageHeight = 0;

        // Bind methods
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onWindowMouseUp = this.onWindowMouseUp.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.resetDimensions = this.resetDimensions.bind(this);

        // Setup shadow DOM
        const shadow = this.attachShadow({ mode: 'open' });
        shadow.appendChild(template.content.cloneNode(true));

        this.firstElement = shadow.getElementById('first');
        this.handleElement = shadow.getElementById('handle');
      }

      set handle(value) {
        this.dragByHandle = value.toString().toLowerCase() !== 'false';
      }

      get handle() {
        return this.dragByHandle;
      }

      get value() {
        return this.exposure;
      }

      set value(val) {
        const newVal = parseFloat(val);
        if (newVal !== this.exposure) {
          this.exposure = newVal;
          this.enableTransition();
          this.setExposure();
        }
      }

      get hover() {
        return this.slideOnHover;
      }

      set hover(value) {
        this.slideOnHover = value.toString().toLowerCase() !== 'false';
        this.removeEventListener('mousemove', this.onMouseMove);
        if (this.slideOnHover) {
          this.addEventListener('mousemove', this.onMouseMove);
        }
      }

      get direction() {
        return this.slideDirection;
      }

      set direction(value) {
        this.slideDirection = value.toString().toLowerCase();
        this.slide(0);
        this.firstElement.classList.remove(...DIRECTIONS);
        if (DIRECTIONS.includes(this.slideDirection)) {
          this.firstElement.classList.add(this.slideDirection);
        }
      }

      static get observedAttributes() {
        return ['hover', 'direction'];
      }

      connectedCallback() {
        if (!this.hasAttribute('tabindex')) {
          this.tabIndex = 0;
        }

        this.addEventListener('dragstart', (e) => {
          e.preventDefault();
          return false;
        });

        new ResizeObserver(this.resetDimensions).observe(this);

        this.setExposure(0);

        this.keyboard =
          this.hasAttribute('keyboard') && this.getAttribute('keyboard') === 'disabled'
            ? 'disabled'
            : 'enabled';

        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('keyup', this.onKeyUp);
        this.addEventListener('focus', this.onFocus);
        this.addEventListener('blur', this.onBlur);
        this.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.addEventListener('touchend', this.onTouchEnd);
        this.addEventListener('mousedown', this.onMouseDown);

        this.handle = this.hasAttribute('handle') ? this.getAttribute('handle') : this.dragByHandle;
        this.hover = this.hasAttribute('hover') ? this.getAttribute('hover') : this.slideOnHover;
        this.direction = this.hasAttribute('direction')
          ? this.getAttribute('direction')
          : this.slideDirection;

        this.resetDimensions();

        if (!this.classList.contains(RENDERED_CLASS)) {
          this.classList.add(RENDERED_CLASS);
        }
      }

      disconnectedCallback() {
        if (this.transitionTimer) {
          window.clearTimeout(this.transitionTimer);
        }
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'hover') {
          this.hover = newValue;
        }
        if (name === 'direction') {
          this.direction = newValue;
        }
        if (name === 'keyboard') {
          this.keyboard = newValue === 'disabled' ? 'disabled' : 'enabled';
        }
      }

      onMouseMove(e) {
        if (this.isMouseDown || this.slideOnHover) {
          const point = getMousePagePoint(e);
          this.slideToPage(point);
        }
      }

      onMouseDown(e) {
        if (this.slideOnHover) return;
        if (this.handle && !isElementAffected(this.handleElement, e)) return;

        e.preventDefault();
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mouseup', this.onWindowMouseUp);

        this.isMouseDown = true;
        this.enableTransition();

        const point = getMousePagePoint(e);
        this.slideToPage(point);
        this.focus();

        this.bodyUserSelectStyle = window.document.body.style.userSelect;
        this.bodyWebkitUserSelectStyle = window.document.body.style.webkitUserSelect;
        window.document.body.style.userSelect = 'none';
        window.document.body.style.webkitUserSelect = 'none';
      }

      onWindowMouseUp() {
        this.isMouseDown = false;
        window.document.body.style.userSelect = this.bodyUserSelectStyle;
        window.document.body.style.webkitUserSelect = this.bodyWebkitUserSelectStyle;
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mouseup', this.onWindowMouseUp);
      }

      onTouchStart(e) {
        if (this.dragByHandle && !isElementAffected(this.handleElement, e)) return;
        this.touchStartPoint = getTouchPagePoint(e);
        if (this.isFocused) {
          this.enableTransition();
          this.slideToPage(this.touchStartPoint);
        }
      }

      onTouchMove(e) {
        if (this.touchStartPoint === null) return;

        const currentPoint = getTouchPagePoint(e);

        if (this.isTouchComparing) {
          this.slideToPage(currentPoint);
          e.preventDefault();
          return false;
        }

        if (!this.hasTouchMoved) {
          const yDiff = Math.abs(currentPoint.y - this.touchStartPoint.y);
          const xDiff = Math.abs(currentPoint.x - this.touchStartPoint.x);

          if (
            (this.slideDirection === 'horizontal' && yDiff < xDiff) ||
            (this.slideDirection === 'vertical' && yDiff > xDiff)
          ) {
            this.isTouchComparing = true;
            this.focus();
            this.slideToPage(currentPoint);
            e.preventDefault();
            return false;
          }

          this.hasTouchMoved = true;
        }
      }

      onTouchEnd() {
        this.isTouchComparing = false;
        this.hasTouchMoved = false;
        this.touchStartPoint = null;
      }

      onBlur() {
        this.stopSlideAnimation();
        this.isFocused = false;
        this.firstElement.classList.remove('focused');
      }

      onFocus() {
        this.isFocused = true;
        this.firstElement.classList.add('focused');
      }

      onKeyDown(e) {
        if (this.keyboard === 'disabled') return;

        const direction = ARROW_KEYS[e.key];
        if (this.animationDirection !== direction && direction !== undefined) {
          this.animationDirection = direction;
          this.startSlideAnimation();
        }
      }

      onKeyUp(e) {
        if (this.keyboard === 'disabled') return;

        const direction = ARROW_KEYS[e.key];
        if (direction !== undefined && this.animationDirection === direction) {
          this.stopSlideAnimation();
        }
      }

      resetDimensions() {
        this.imageWidth = this.offsetWidth;
        this.imageHeight = this.offsetHeight;
      }

      setExposure(delta = 0) {
        this.exposure = clamp(this.exposure + delta);
        this.firstElement.style.setProperty('--exposure', 100 - this.exposure + '%');
      }

      slide(delta = 0) {
        this.setExposure(delta);
        const event = new Event('slide');
        this.dispatchEvent(event);
      }

      slideToPage(point) {
        if (this.slideDirection === 'horizontal') {
          this.slideToPageX(point.x);
        }
        if (this.slideDirection === 'vertical') {
          this.slideToPageY(point.y);
        }
      }

      slideToPageX(pageX) {
        const x = pageX - this.getBoundingClientRect().left - window.scrollX;
        this.exposure = (x / this.imageWidth) * 100;
        this.slide(0);
      }

      slideToPageY(pageY) {
        const y = pageY - this.getBoundingClientRect().top - window.scrollY;
        this.exposure = (y / this.imageHeight) * 100;
        this.slide(0);
      }

      enableTransition() {
        this.firstElement.style.setProperty('--transition-time', '100ms');
        this.transitionTimer = window.setTimeout(() => {
          this.firstElement.style.setProperty('--transition-time', 'var(--default-transition-time)');
          this.transitionTimer = null;
        }, 100);
      }

      startSlideAnimation() {
        let lastTime = null;
        let savedDirection = this.animationDirection;

        this.firstElement.style.setProperty('--transition-time', 'var(--keyboard-transition-time)');

        const animate = (currentTime) => {
          if (this.animationDirection === 0 || savedDirection !== this.animationDirection) {
            return;
          }

          if (lastTime === null) {
            lastTime = currentTime;
          }

          const frameDelta = ((currentTime - lastTime) / 16.666666666666668) * this.animationDirection;
          this.slide(frameDelta);

          setTimeout(() => window.requestAnimationFrame(animate), 0);
          lastTime = currentTime;
        };

        window.requestAnimationFrame(animate);
      }

      stopSlideAnimation() {
        this.animationDirection = 0;
        this.firstElement.style.setProperty('--transition-time', 'var(--default-transition-time)');
      }
    }
  );
}
